import React, { useState, useRef, useEffect } from 'react';

interface Option {
  value: string;
  label: string;
}

interface SelectDrawerProps {
  id?: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void; // Ajout de l'événement onBlur
  options: Option[];
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  className?: string;
  icon?: React.ReactNode;
  maxHeight?: string;
  allowCustomInput?: boolean; // Nouvelle prop pour autoriser la saisie manuelle
}

const SelectDrawer: React.FC<SelectDrawerProps> = ({
  id,
  name,
  value,
  onChange,
  onBlur,
  options,
  placeholder,
  label,
  required = false,
  disabled = false,
  error,
  className = '',
  icon,
  maxHeight = '250px',
  allowCustomInput = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [inputValue, setInputValue] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Fermer le menu déroulant lors d'un clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Mettre à jour le label sélectionné et la valeur de l'input quand la valeur change
  useEffect(() => {
    const option = options.find(opt => opt.value === value);
    setSelectedLabel(option ? option.label : placeholder || '');
    setInputValue(option ? option.label : value || ''); // Pour l'input text
  }, [value, options, placeholder]);
  
  // Gérer le changement de sélection
  const handleSelectOption = (option: Option) => {
    const event = {
      target: {
        name,
        value: option.value
      }
    } as React.ChangeEvent<HTMLSelectElement>;
    
    onChange(event);
    setInputValue(option.label); // Mettre à jour l'input avec le label sélectionné
    setIsOpen(false);
  };

  // Gérer la saisie manuelle
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.target.value;
    setInputValue(inputVal);
    
    // Si la valeur saisie correspond exactement à un label existant
    const matchingOption = options.find(opt => opt.label.toLowerCase() === inputVal.toLowerCase());
    
    if (matchingOption) {
      // Si on trouve une correspondance exacte, on utilise sa valeur
      const event = {
        target: {
          name,
          value: matchingOption.value
        }
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(event);
    } else {
      // Sinon, on utilise la valeur saisie directement
      const event = {
        target: {
          name,
          value: inputVal
        }
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(event);
    }
  };
  
  // Ouvrir la liste au focus sur l'input
  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };
  
  return (
    <div className="w-full">
      {label && (
        <label 
          htmlFor={id || name} 
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div ref={dropdownRef} className={`relative w-full ${className}`}>
        {allowCustomInput ? (
          <div className="relative flex items-center w-full">
            <input
              type="text"
              id={id || name}
              name={name}
              value={inputValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={onBlur}
              disabled={disabled}
              placeholder={placeholder || 'Sélectionner ou saisir...'}
              className={`
                w-full rounded-md border border-gray-300 dark:border-gray-700 
                bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                shadow-sm focus:border-primary-500 focus:ring-primary-500 
                disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-900 
                dark:disabled:text-gray-400 text-sm py-2 px-3
                ${icon ? 'pl-10' : ''}
                ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                ${className}
                transition-all duration-200 ease-in-out
                hover:bg-gray-50 dark:hover:bg-gray-750
                pr-10
              `}
              aria-haspopup="listbox"
            />
            {icon && (
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">
                {icon}
              </span>
            )}
            <button 
              type="button" 
              onClick={() => !disabled && setIsOpen(!isOpen)}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 focus:outline-none"
              tabIndex={-1}
              aria-label="Ouvrir la liste"
            >
              <svg
                className={`h-4 w-4 fill-current text-gray-500 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
              </svg>
            </button>
          </div>
        ) : (
          <button
            type="button"
            id={id || name}
            disabled={disabled}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            className={`
              flex items-center justify-between w-full rounded-md border border-gray-300 dark:border-gray-700 
              bg-white dark:bg-gray-800 text-gray-900 dark:text-white
              shadow-sm focus:border-primary-500 focus:ring-primary-500 
              disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-900 
              dark:disabled:text-gray-400 text-sm py-2 px-3
              ${icon ? 'pl-10' : ''}
              ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
              ${className}
              transition-all duration-200 ease-in-out
              hover:bg-gray-50 dark:hover:bg-gray-750
            `}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
          >
            {icon && (
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">
                {icon}
              </span>
            )}
            <span className="block truncate text-left">
              {selectedLabel || placeholder || 'Sélectionner...'}
            </span>
            <span className="pointer-events-none ml-2">
              <svg
                className={`h-4 w-4 fill-current transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
              </svg>
            </span>
          </button>
        )}

        {/* Liste déroulante avec défilement - Positionnement absolu pour les drawers */}
        {isOpen && (
          <div
            className="rounded-md bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700"
            style={{
              position: 'absolute', // Position absolue pour les drawers
              width: '100%',
              top: '100%', // Juste en dessous du bouton
              left: 0,
              maxHeight,
              overflowY: 'auto',
              zIndex: 50 // Z-index élevé mais pas autant que pour les modals
            }}
          >
            <ul
              className="py-1 overflow-auto text-base"
              style={{ maxHeight }}
              role="listbox"
              tabIndex={-1}
            >
              {placeholder && (
                <li
                  className={`
                    cursor-pointer select-none relative py-2 pl-3 pr-9 text-gray-500 dark:text-gray-400
                    hover:bg-gray-100 dark:hover:bg-gray-700
                  `}
                  role="option"
                  onClick={() => handleSelectOption({ value: '', label: placeholder })}
                >
                  {placeholder}
                </li>
              )}
              {options.map((option, index) => (
                <li
                  key={`${option.value}-${index}`}
                  className={`
                    cursor-pointer select-none relative py-2 pl-3 pr-9
                    ${option.value === value ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100' : 'text-gray-900 dark:text-white'}
                    hover:bg-gray-100 dark:hover:bg-gray-700
                  `}
                  role="option"
                  aria-selected={option.value === value}
                  onClick={() => handleSelectOption(option)}
                >
                  <span className="block truncate">{option.label}</span>
                  {option.value === value && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-primary-600 dark:text-primary-400">
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Champ select caché pour la compatibilité avec les formulaires */}
        <select
          id={`${id || name}-hidden`}
          name={name}
          value={value}
          onChange={onChange}
          disabled={disabled}
          required={required}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
        >
          {placeholder && (
            <option key="placeholder" value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option, index) => (
            <option key={`${option.value}-${index}`} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      
      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-500">{error}</p>
      )}
    </div>
  );
};

export default SelectDrawer;
